/**
 * WebSocket Manager
 *
 * Manages WebSocket connection with:
 * - Auto-reconnect with exponential backoff
 * - Heartbeat/keepalive
 * - Message handlers for different event types
 * - Connection state tracking
 * - Pending message queue with ACK-based delivery
 * - Auto-flush on reconnect
 */

import { getWsUrl } from '../utils/constants';
import * as storage from '../utils/storage';
import { refreshAccessToken } from '../api/client';
import { AppState, Platform, type AppStateStatus } from 'react-native';

export type WsState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

type MessageHandler = (data: any) => void;

interface PendingMessage {
  msgId: string;
  type: string;
  payload: any;
  timestamp: number;
}

let _msgCounter = 0;

const CONNECTION_TIMEOUT_MS = 6000;
const HEARTBEAT_INTERVAL_MS = 15000;
const HEARTBEAT_TIMEOUT_MS = 6000;
const MAX_RECONNECT_DELAY_MS = 8000;

/** Generate a unique client message ID */
export function generateMsgId(): string {
  return `c_${Date.now()}_${++_msgCounter}`;
}

export class WebSocketManager {
  private _ws: WebSocket | null = null;
  private _handlers: Map<string, Set<MessageHandler>> = new Map();
  private _stateListeners: Set<(state: WsState) => void> = new Set();
  private _state: WsState = 'disconnected';
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private _reconnectAttempts = 0;
  private _baseDelay = 1000;
  private _connectLock = false;
  private _shouldReconnect = false;
  private _connectAttemptId = 0;
  private _connectionTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

  /** Queue of messages awaiting ACK from server */
  private _pending: PendingMessage[] = [];

  get state(): WsState {
    return this._state;
  }

  get isConnected(): boolean {
    return this._state === 'connected';
  }

  /**
   * Connect to WebSocket server.
   */
  async connect(): Promise<void> {
    this._shouldReconnect = true;
    if (this._ws?.readyState === WebSocket.OPEN || this._ws?.readyState === WebSocket.CONNECTING) return;

    if (this._connectLock) return;
    this._connectLock = true;
    const connectAttemptId = ++this._connectAttemptId;

    try {
      const token = await storage.getAccessToken();
      if (!this._shouldReconnect || connectAttemptId !== this._connectAttemptId) return;
      if (!token) {
        this._setState('disconnected');
        return;
      }

      if (this._ws?.readyState === WebSocket.OPEN || this._ws?.readyState === WebSocket.CONNECTING) return;

      if (this._ws) {
        this._ws.onopen = null;
        this._ws.onmessage = null;
        this._ws.onclose = null;
        this._ws.onerror = null;
      }

      this._setState('connecting');

      const ws = new WebSocket(`${getWsUrl()}?token=${token}`);
      this._ws = ws;

      // Ensure we don't hang in "connecting" forever
      if (this._connectionTimeoutTimer) clearTimeout(this._connectionTimeoutTimer);
      this._connectionTimeoutTimer = setTimeout(() => {
        if (this._ws === ws && ws.readyState === WebSocket.CONNECTING) {
          // Clear the reference before close(): closing a CONNECTING socket can
          // throw on some Android network stacks and otherwise strand retries.
          this._connectionTimeoutTimer = null;
          this._discardSocket(ws);
          this._attemptReconnect();
        }
      }, CONNECTION_TIMEOUT_MS);

      ws.onopen = () => {
        if (this._ws !== ws) return;
        this._clearConnectionTimeout();
        
        this._setState('connected');
        this._reconnectAttempts = 0;
        this._startHeartbeat();
        // Flush pending messages that weren't ACKed before disconnect
        this._flushPending();
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') {
            this._clearHeartbeatTimeout();
          }
          // Handle ACKs internally — remove from pending queue
          if (data.type === 'ack' && data.payload?.msgId) {
            this._removePending(data.payload.msgId);
          }
          this._dispatch(data.type, data);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = (event: CloseEvent) => {
        if (this._ws !== ws) return;
        this._clearConnectionTimeout();
        this._stopHeartbeat();
        this._ws = null; // Clear so connect() bypasses lock gracefully 
        
        if (event.code === 4001) {
          // Auth failed — token may have expired during disconnect.
          // Attempt to refresh the access token and reconnect instead of giving up.
          this._refreshAndReconnect();
          return;
        }
        if (event.code === 4002) {
          // Server replaced connection (e.g. server restart) — reconnect immediately
          this._reconnectAttempts = 0;
          this._attemptReconnect();
          return;
        }
        
        // For normal transient errors (1001, 1006) try fast reconnect
        this._attemptReconnect();
      };

      ws.onerror = () => {
        // onclose will fire after onerror
      };
    } catch {
      this._attemptReconnect();
    } finally {
      if (connectAttemptId === this._connectAttemptId) {
        this._connectLock = false;
      }
    }
  }

  /** Verify an open socket or immediately replace stale in-flight state. */
  ensureConnected(): void {
    if (!this._shouldReconnect) return;
    if (this._state === 'connected' && this._ws?.readyState === WebSocket.OPEN) {
      this._sendHeartbeatProbe();
      return;
    }
    this.reconnectNow();
  }

  /** Force a fresh socket without briefly reporting a disconnected state. */
  reconnectNow(): void {
    if (!this._shouldReconnect) return;
    this._clearReconnect();
    this._stopHeartbeat();
    this._clearConnectionTimeout();
    this._connectAttemptId++;
    this._connectLock = false;
    this._discardSocket();
    this._setState('reconnecting');
    void this.connect();
  }

  /**
   * Disconnect from WebSocket server.
   */
  disconnect(): void {
    this._shouldReconnect = false;
    this._clearReconnect();
    this._stopHeartbeat();
    this._clearConnectionTimeout();
    this._connectAttemptId++;
    this._connectLock = false;
    this._discardSocket();
    this._setState('disconnected');
  }

  /**
   * Send a raw message to the server (no queue/ACK tracking).
   */
  send(type: string, payload: any): boolean {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return false;
    try {
      this._ws.send(JSON.stringify({ type, payload }));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Send a tracked message — queued if offline, resent on reconnect.
   * Returns the msgId for status tracking.
   */
  sendTracked(type: string, payload: any): { sent: boolean; msgId: string } {
    const msgId = generateMsgId();
    const fullPayload = { ...payload, msgId };
    const pending: PendingMessage = { msgId, type, payload: fullPayload, timestamp: Date.now() };
    this._pending.push(pending);

    const sent = this.send(type, fullPayload);
    if (!sent) {
      // Mark all unsent pending as failed candidates — they'll be flushed on reconnect
    }
    return { sent, msgId };
  }

  /**
   * Send a chat message (tracked).
   */
  sendChat(text: string): { sent: boolean; msgId: string } {
    return this.sendTracked('chat', { text });
  }

  /**
   * Send a button press (tracked).
   */
  sendButton(buttonId: string): { sent: boolean; msgId: string } {
    return this.sendTracked('button', { buttonId });
  }

  /**
   * Send a list selection (tracked).
   */
  sendListSelect(itemId: string): { sent: boolean; msgId: string } {
    return this.sendTracked('list_select', { itemId });
  }

  /**
   * Check if a message is still pending (not ACKed).
   */
  isPending(msgId: string): boolean {
    return this._pending.some(p => p.msgId === msgId);
  }

  /** Remove a pending message when an alternate transport handled it. */
  dropPending(msgId: string): void {
    this._removePending(msgId);
  }

  /**
   * Subscribe to a message type.
   */
  on(type: string, handler: MessageHandler): () => void {
    if (!this._handlers.has(type)) {
      this._handlers.set(type, new Set());
    }
    this._handlers.get(type)!.add(handler);
    return () => {
      this._handlers.get(type)?.delete(handler);
    };
  }

  /**
   * Subscribe to connection state changes.
   *
   * Fires the listener with the current state immediately on subscribe so
   * subscribers (e.g. chatStore.initWebSocket on chat tab mount) don't miss
   * a transition that happened before they registered. Without this, if the
   * connection was opened by authStore.loadSession() before the chat tab
   * mounted, the chat tab would never learn the WS was already 'connected'
   * and the Offline banner would stay up forever.
   */
  onStateChange(listener: (state: WsState) => void): () => void {
    this._stateListeners.add(listener);
    try {
      listener(this._state);
    } catch {
      // Subscriber's own bug — don't break subscription.
    }
    return () => {
      this._stateListeners.delete(listener);
    };
  }

  // ──── Private ────

  private _setState(state: WsState): void {
    this._state = state;
    this._stateListeners.forEach((l) => l(state));
  }

  private _dispatch(type: string, data: any): void {
    const handlers = this._handlers.get(type);
    if (handlers) {
      handlers.forEach((h) => {
        try { h(data); } catch { /* non-fatal */ }
      });
    }
    const wildcard = this._handlers.get('*');
    if (wildcard) {
      wildcard.forEach((h) => {
        try { h(data); } catch { /* non-fatal */ }
      });
    }
  }

  private _removePending(msgId: string): void {
    this._pending = this._pending.filter(p => p.msgId !== msgId);
  }

  /** Resend all pending (unACKed) messages after reconnect */
  private _flushPending(): void {
    // Drop messages older than 5 minutes — they're stale
    const cutoff = Date.now() - 5 * 60 * 1000;
    this._pending = this._pending.filter(p => p.timestamp > cutoff);

    for (const p of this._pending) {
      this.send(p.type, p.payload);
    }
  }

  private _attemptReconnect(): void {
    if (!this._shouldReconnect) return;
    if (this._ws?.readyState === WebSocket.OPEN || this._ws?.readyState === WebSocket.CONNECTING) return;
    if (this._reconnectTimer) return;

    this._setState('reconnecting');
    // First attempt immediately reconnects (100ms) for fast dev cycles/server updates
    const delay = this._reconnectAttempts === 0 
      ? 100 
      : Math.min(this._baseDelay * Math.pow(2, this._reconnectAttempts - 1), MAX_RECONNECT_DELAY_MS);
      
    this._reconnectAttempts++;

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._shouldReconnect) void this.connect();
    }, delay);
  }

  /**
   * On WS auth failure (4001): refresh the access token via REST,
   * then reconnect WS with the new token. If refresh fails, fall
   * back to normal reconnect backoff (token may recover later).
   */
  private async _refreshAndReconnect(): Promise<void> {
    const refreshAttemptId = this._connectAttemptId;
    this._setState('reconnecting');
    try {
      const data = await refreshAccessToken();
      if (!this._shouldReconnect || refreshAttemptId !== this._connectAttemptId) return;
      await storage.setAccessToken(data.accessToken);
      if (data.refreshToken) {
        await storage.setRefreshToken(data.refreshToken);
      }
      // Got a fresh access token — reconnect immediately
      this._ws = null;
      this._clearReconnect();
      this._reconnectAttempts = 0;
      void this.connect();
    } catch {
      if (!this._shouldReconnect || refreshAttemptId !== this._connectAttemptId) {
        this._setState('disconnected');
        return;
      }
      // Refresh failed (network error or server down) — fall back to reconnect backoff
      this._ws = null;
      this._attemptReconnect();
    }
  }

  private _clearReconnect(): void {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._reconnectAttempts = 0;
  }

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    this._heartbeatTimer = setInterval(() => {
      this._sendHeartbeatProbe();
    }, HEARTBEAT_INTERVAL_MS);
  }

  private _stopHeartbeat(): void {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    this._clearHeartbeatTimeout();
  }

  private _sendHeartbeatProbe(): void {
    if (this._heartbeatTimeoutTimer) return;
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN || !this.send('ping', {})) {
      this.reconnectNow();
      return;
    }

    const socket = this._ws;
    this._heartbeatTimeoutTimer = setTimeout(() => {
      this._heartbeatTimeoutTimer = null;
      if (this._ws === socket && this._state === 'connected') {
        this.reconnectNow();
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private _clearHeartbeatTimeout(): void {
    if (!this._heartbeatTimeoutTimer) return;
    clearTimeout(this._heartbeatTimeoutTimer);
    this._heartbeatTimeoutTimer = null;
  }

  private _clearConnectionTimeout(): void {
    if (!this._connectionTimeoutTimer) return;
    clearTimeout(this._connectionTimeoutTimer);
    this._connectionTimeoutTimer = null;
  }

  private _discardSocket(socket = this._ws): void {
    if (!socket) return;
    if (this._ws === socket) this._ws = null;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    try {
      socket.close();
    } catch {
      // The reference is already cleared, so retry can continue safely.
    }
  }
}

// Singleton
export const wsManager = new WebSocketManager();

// Reconnect WS when app returns to foreground (skip on web — AppState may not be available)
if (Platform.OS !== 'web') {
  AppState.addEventListener('change', (nextState: AppStateStatus) => {
    if (nextState === 'active') {
      wsManager.ensureConnected();
    }
  });
} else if (typeof document !== 'undefined') {
  // Web: reconnect when tab becomes visible again
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      wsManager.ensureConnected();
    }
  });
}
